import { ArrowRight, Check, Share2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  EditorialContainer,
  TopSpacer,
  usePublicationMeta,
  useSharePage,
} from '../../../components/editorial/PublicationShared';
import { formatInsightPublishedAt } from '../content';
import type { InsightPost } from '../types';
import { insightsV2ServicesBackground } from '../styles';
import BlockRenderer from './BlockRenderer';
import InsightImage from './InsightImage';

const Page = styled.main`
  min-height: 100vh;
  ${insightsV2ServicesBackground}
  color: #132235;
`;

const Hero = styled.section`
  padding: 0 0 3.5rem;
`;

const HeroHeader = styled.div`
  display: grid;
  gap: 1rem;
  margin-bottom: 1.35rem;

  @media (max-width: 980px) {
    justify-items: center;
    text-align: center;
  }
`;

const HeroShell = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(320px, 0.95fr);
  gap: 2.2rem;
  align-items: center;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const Copy = styled.div`
  display: grid;
  gap: 0.85rem;
  align-content: center;

  @media (max-width: 980px) {
    justify-items: center;
    text-align: center;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: rgba(20, 34, 53, 0.64);
  font-size: 0.9rem;
  font-weight: 800;
  text-decoration: none;

  &:hover {
    color: #132235;
  }

  @media (max-width: 980px) {
    justify-self: center;
  }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 980px) {
    flex-direction: column;
    justify-content: center;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
  color: rgba(20, 34, 53, 0.6);
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;

  @media (max-width: 980px) {
    justify-content: center;
  }
`;

const AuthorMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

const AuthorAvatar = styled.span<{ $src?: string }>`
  width: 1.8rem;
  height: 1.8rem;
  min-width: 1.8rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid rgba(20, 34, 53, 0.12);
  background: rgba(255, 255, 255, 0.52);
  color: #132235;
  font-size: 0.68rem;
  font-weight: 900;
  text-transform: uppercase;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const Dot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: rgba(20, 34, 53, 0.22);
`;

const Title = styled.h1<{ $ink: string }>`
  margin: 0;
  font-family: var(--font-serif);
  font-size: clamp(2.9rem, 5.5vw, 5.8rem);
  line-height: 0.92;
  letter-spacing: -0.05em;
  color: ${({ $ink }) => $ink};
  max-width: 10.5ch;

  @media (max-width: 980px) {
    text-align: center;
    max-width: none;
  }
`;

const VisualColumn = styled.div`
  display: grid;
  gap: 1rem;
  align-content: center;

  @media (max-width: 980px) {
    justify-items: center;
    text-align: center;
  }
`;

const Subtitle = styled.p<{ $mutedInk: string }>`
  margin: 0;
  max-width: 38rem;
  color: ${({ $mutedInk }) => $mutedInk};
  font-size: 1.08rem;
  line-height: 1.78;

  @media (max-width: 980px) {
    text-align: center;
  }
`;

const ShareButton = styled.button<{ $accent: string }>`
  width: 2.9rem;
  height: 2.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(20, 34, 53, 0.1);
  border-radius: 999px;
  padding: 0;
  background: rgba(255, 255, 255, 0.36);
  color: ${({ $accent }) => $accent};
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease, color 160ms ease, transform 160ms ease;

  &:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.62);
    border-color: rgba(20, 34, 53, 0.18);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(224, 161, 94, 0.18);
  }
`;

const Body = styled.section`
  padding: 0 0 5.5rem;
`;

const BodyGrid = styled.div<{ $hasRail: boolean }>`
  display: grid;
  grid-template-columns: ${({ $hasRail }) => ($hasRail ? 'minmax(220px, 260px) minmax(0, 1fr)' : 'minmax(0, 1fr)')};
  gap: 2.4rem;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Rail = styled.aside`
  align-self: start;
  position: sticky;
  top: 108px;
  display: grid;
  gap: 1.2rem;

  @media (max-width: 980px) {
    position: static;
  }
`;

const RailSection = styled.section`
  padding-bottom: 1.25rem;
  margin-bottom: 1.25rem;
  border-bottom: 1px solid rgba(16, 22, 37, 0.12);

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

const RailLabel = styled.p`
  margin: 0 0 0.7rem;
  font-size: 0.74rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 800;
  color: rgba(16, 22, 37, 0.52);
`;

const RailList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.75rem;
`;

const RailListItem = styled.li`
  color: rgba(16, 22, 37, 0.86);
  font-size: 0.98rem;
  line-height: 1.6;
  font-weight: 700;
`;

const ArticleBody = styled.div`
  display: grid;
  gap: 1.9rem;
  min-width: 0;
`;

const EmptyBody = styled.div`
  max-width: 46rem;
  margin: 0 auto;
  padding: 1.4rem;
  border-radius: 24px;
  border: 1px dashed rgba(20, 34, 53, 0.16);
  color: rgba(20, 34, 53, 0.66);
  background: rgba(255, 255, 255, 0.4);
`;

const NextBlogRow = styled.div`
  max-width: 46rem;
  margin: 2.8rem 0;
  padding-top: 1.4rem;
  border-top: 1px solid rgba(20, 34, 53, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 760px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const NextBlogCopy = styled.div`
  display: grid;
  gap: 0.28rem;
`;

const NextBlogEyebrow = styled.span`
  color: rgba(20, 34, 53, 0.58);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const NextBlogTitle = styled(Link)<{ $ink: string }>`
  color: ${({ $ink }) => $ink};
  text-decoration: none;
  font-family: var(--font-serif);
  font-size: clamp(1.45rem, 2.4vw, 2rem);
  line-height: 1;
  letter-spacing: -0.035em;

  &:hover {
    text-decoration: underline;
    text-underline-offset: 0.16em;
  }
`;

const NextBlogButton = styled(Link)<{ $accent: string }>`
  width: 2.9rem;
  height: 2.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(20, 34, 53, 0.12);
  color: ${({ $accent }) => $accent};
  background: transparent;
  text-decoration: none;
  transition: transform 160ms ease, border-color 160ms ease, background 160ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: ${({ $accent }) => $accent};
    background: rgba(255, 255, 255, 0.44);
  }
`;

type InsightArticleTemplateProps = {
  post: InsightPost;
  nextPost?: InsightPost | null;
};

export default function InsightArticleTemplate({ post, nextPost }: InsightArticleTemplateProps) {
  const seoTitle = post.seo.title ?? post.title;
  const seoDescription = post.seo.description ?? post.excerpt;
  usePublicationMeta(seoTitle, seoDescription);

  const { share, state } = useSharePage(seoTitle);
  const hasRail = post.keyPoints.length > 0;
  const authorInitials =
    post.author
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || 'CB';

  return (
    <Page>
      <TopSpacer />

      <Hero>
        <EditorialContainer>
          <HeroHeader>
            <BackLink to="/publications">Back to publications</BackLink>
            <MetaRow>
              <Meta>
                <span>CyBard Journal</span>
                <Dot />
                <AuthorMeta>
                  <AuthorAvatar $src={post.authorAvatarUrl}>
                    {post.authorAvatarUrl ? <img src={post.authorAvatarUrl} alt={post.author} /> : authorInitials}
                  </AuthorAvatar>
                  <span>{post.author}</span>
                </AuthorMeta>
                <Dot />
                <span>{formatInsightPublishedAt(post.publishedAt)}</span>
                <Dot />
                <span>{post.readTime}</span>
              </Meta>

              <ShareButton
                type="button"
                $accent={post.theme.accent}
                onClick={share}
                aria-label={state === 'copied' ? 'Link copied' : 'Share article'}
                title={state === 'copied' ? 'Link copied' : 'Share article'}
              >
                {state === 'copied' ? <Check size={18} aria-hidden="true" /> : <Share2 size={18} aria-hidden="true" />}
              </ShareButton>
            </MetaRow>
          </HeroHeader>

          <HeroShell>
            <Copy>
              <Title $ink={post.theme.ink}>{post.title}</Title>
            </Copy>

            <VisualColumn>
              <InsightImage
                src={post.coverImageUrl}
                alt={post.coverImageAlt}
                width="full"
                fit="cover"
                minHeight={420}
                rounded={34}
                accent={post.theme.accent}
                surface={post.theme.surface}
                fallbackLabel="Cover image"
              />
              {post.subtitle ? <Subtitle $mutedInk={post.theme.mutedInk}>{post.subtitle}</Subtitle> : null}
            </VisualColumn>
          </HeroShell>
        </EditorialContainer>
      </Hero>

      <Body>
        <EditorialContainer>
          <BodyGrid $hasRail={hasRail}>
            {hasRail ? (
              <Rail>
                <RailSection>
                  <RailLabel>Key points</RailLabel>
                  <RailList>
                    {post.keyPoints.map((point) => (
                      <RailListItem key={point}>{point}</RailListItem>
                    ))}
                  </RailList>
                </RailSection>
              </Rail>
            ) : null}

            <ArticleBody>
              {post.blocks.length ? (
                <BlockRenderer blocks={post.blocks} theme={post.theme} />
              ) : (
                <EmptyBody>This article has no content blocks yet.</EmptyBody>
              )}
            </ArticleBody>
          </BodyGrid>

          {nextPost ? (
            <NextBlogRow>
              <NextBlogCopy>
                <NextBlogEyebrow>Check out our next blog</NextBlogEyebrow>
                <NextBlogTitle to={`/publications/${nextPost.slug}`} $ink={nextPost.theme.ink}>
                  {nextPost.title}
                </NextBlogTitle>
              </NextBlogCopy>
              <NextBlogButton
                to={`/publications/${nextPost.slug}`}
                $accent={nextPost.theme.accent}
                aria-label={`Open next blog: ${nextPost.title}`}
                title={`Open next blog: ${nextPost.title}`}
              >
                <ArrowRight size={18} aria-hidden="true" />
              </NextBlogButton>
            </NextBlogRow>
          ) : null}
        </EditorialContainer>
      </Body>
    </Page>
  );
}
